import { Component, ElementRef, effect, input, output, signal, viewChild } from '@angular/core';
import { Signaturs, step } from '@app/models/chip.model';

import { AlertComponent } from '../alert/alert.component';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import SignaturePad from 'signature_pad';
import { StepperModule } from 'primeng/stepper';
import { TranslatePipe } from '@ngx-translate/core';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'app-signature',
    templateUrl: './signature.component.html',
    styleUrls: ['./signature.component.scss'],
    imports: [
    TranslatePipe,
        StepperModule,
        ButtonModule,
        DialogModule,
        AlertComponent
    ],
})
export class SignatureCaptureComponent {
    readonly visible = input<boolean>(false);
    readonly signatures = input<step[]>(['veterinarian', 'owner']);

    readonly closeModal = output<void>();
    readonly saveSignaturesOutput = output<Signaturs>();

    readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');

    signaturePad: SignaturePad | null = null;
    readonly showAlert = signal(false);
    firstStep = 1;
    lastStep = 0;
    currentStep = this.firstStep;
    savedSignatues: Signaturs = {
        veterinarian: null,
        owner: null,
        previousOwner: null,
    };

    constructor() {
        effect(() => {
            if (this.visible()) {
                setTimeout(() => {
                    const canvasRef = this.canvasRef();

                    if (canvasRef) {
                        const canvas = canvasRef.nativeElement;
                        this.adjustCanvasForHiDPI(canvas);

                        this.signaturePad = new SignaturePad(canvas, {
                            backgroundColor: '#fff',
                            penColor: '#000',
                        });
                    }
                });
            }
            this.lastStep = this.signatures().length;
        });
    }

    private adjustCanvasForHiDPI(canvas: HTMLCanvasElement): void {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d')?.scale(ratio, ratio);
    }

    clear(): void {
        if (this.signaturePad) {
            this.signaturePad.clear();
            if (this.signaturePad) {
            this.savedSignatues[this.signatures()[this.currentStep - 1]] = null;
        }
        }
    }

    prevStep(): void {
        if (this.signaturePad && !this.signaturePad.isEmpty() && this.currentStep > this.firstStep) {
            this.saveSignatureToObject();
            this.signaturePad.clear();
            this.currentStep -= 1;
            this.showSignatureInPadFromObject();
        }
    }

    nextStep(): void {
        if (this.signaturePad && !this.signaturePad.isEmpty() && this.currentStep < this.lastStep) {
            this.saveSignatureToObject();
            this.signaturePad.clear();
            this.currentStep += 1;
            this.showSignatureInPadFromObject();
        }
    }

    showSignatureInPadFromObject(): void {
        const signature = this.savedSignatues[this.signatures()[this.currentStep - 1]];
        if (signature && this.signaturePad) {
            this.signaturePad.fromDataURL(signature);
        }
    }

    saveSignatureToObject(): void {
        if (this.signaturePad) {
            this.savedSignatues[this.signatures()[this.currentStep - 1]] = this.signaturePad.toDataURL('image/png');
        }
    }

    // check if all signatures defined
    showAlertOnEmptySignature(): void {
        this.showAlert.set(false);
        this.signatures().forEach(el => {
            if (this.savedSignatues[el] === null) {
                this.showAlert.set(true);
            }
        });
    }

    handleModalClose(): void {
        this.showAlert.set(false);
        this.currentStep = this.firstStep;
        this.closeModal.emit();
    }

    save(): void {
        this.saveSignatureToObject();
        this.showAlertOnEmptySignature();
        if (!this.showAlert()) {
            this.closeModal.emit();
            this.showAlert.set(false);
            this.currentStep = this.firstStep;
            this.saveSignaturesOutput.emit(this.savedSignatues);
        }
    }
}
